import { apiFetch } from "./config.js";
import { fetchSourceDocumentLink } from "./ingestion.js";
/** The two dashboard counts, plus the watch notes by verdict (kept apart from them). queryKey `["cil", "summary"]`. */
export async function getCilSummary(signal) {
    return apiFetch("/cil/summary", { method: "GET", signal });
}
/**
 * Watch notes, newest document first: other parties' rulings and notices that
 * may matter to HP. Not change events. queryKey `["cil", "watch-notes", params]`.
 * A current server also sends each note's own `document`, its `facts` with
 * their page cites and its `status_history`; an older one does not, which is
 * why those fields are optional on `WatchNote`. A cite's document opens through
 * `getSourceDocumentPdfLink(cite.document_id, cite.page)`.
 */
export async function fetchWatchNotes(params = {}, signal) {
    // Always sent explicitly so the request says which list it wants, whatever the server default becomes.
    const query = `include_no_match=${params.includeNoMatch ? "true" : "false"}`;
    return apiFetch(`/cil/watch-notes?${query}`, { method: "GET", signal });
}
/** The people who did the most review work. queryKey `["cil", "top-reviewers"]`. */
export async function getCilTopReviewers(limit = 5, signal) {
    return apiFetch(`/cil/top-reviewers?limit=${limit}`, { method: "GET", signal });
}
/** Changed events, newest first. queryKey `["cil", "change-events", params]`. */
export async function listCilChangeEvents(params = {}, signal) {
    const search = new URLSearchParams();
    if (params.direction)
        search.set("direction", params.direction);
    if (params.view)
        search.set("view", params.view);
    if (params.strength)
        search.set("strength", params.strength);
    if (params.eventKey)
        search.set("event_key", params.eventKey);
    if (params.jurisdiction)
        search.set("jurisdiction", params.jurisdiction);
    if (params.ingestionJobId)
        search.set("ingestion_job_id", params.ingestionJobId);
    const query = search.toString();
    return apiFetch(`/cil/change-events${query ? `?${query}` : ""}`, { method: "GET", signal });
}
export async function getCilChangeEvent(id, signal) {
    return apiFetch(`/cil/change-events/${id}`, { method: "GET", signal });
}
/** Full justification: status, dated timeline, checks, next steps. queryKey `["cil", "explanation", id]`. */
export async function getCilChangeEventExplanation(id, signal) {
    return apiFetch(`/cil/change-events/${id}/explanation`, { method: "GET", signal });
}
/** The HP order an event was compared with, consolidated: HP's position per year and every claimed-vs-allowed line. queryKey `["cil", "order-summary", id]`. */
export async function getCilOrderSummary(id, signal) {
    return apiFetch(`/cil/change-events/${id}/order-summary`, { method: "GET", signal });
}
/** True-up ledger card: material gaps and the total HP was short. */
export async function getTrueupSummary(signal) {
    return apiFetch("/trueup/summary", { method: "GET", signal });
}
/** Every line of HP's true-up orders: claimed vs allowed, why it was cut, what happened next. */
export async function getTrueupLedger(signal) {
    return apiFetch("/trueup/ledger", { method: "GET", signal });
}
/** A reviewer edits (or confirms) why a true-up item was cut. Invalidate `["trueup"]` after. */
export async function updateTrueupReason(id, body, signal) {
    return apiFetch(`/trueup/lines/${id}/reason`, { method: "PATCH", body, signal });
}
/** Every ruling read and what became of it (no ruling may go unnoticed). */
export async function getCilCoverage(signal) {
    return apiFetch("/cil/coverage", { method: "GET", signal });
}
/** Confirm, dismiss (reason required) or reopen a changed event. Invalidate `["cil"]` after. */
export async function updateCilChangeEvent(id, body, signal) {
    return apiFetch(`/cil/change-events/${id}`, { method: "PATCH", body, signal });
}
export async function listCilDecisions(params = {}, signal) {
    const search = new URLSearchParams();
    if (params.eventKey)
        search.set("event_key", params.eventKey);
    if (params.isHp !== undefined)
        search.set("is_hp", String(params.isHp));
    if (params.reviewState)
        search.set("review_state", params.reviewState);
    if (params.sourceDocumentId)
        search.set("source_document_id", params.sourceDocumentId);
    const query = search.toString();
    return apiFetch(`/cil/decisions${query ? `?${query}` : ""}`, { method: "GET", signal });
}
/** Signed, Range-capable link to the PDF a decision was read from, opened at its page; `null` when not stored. */
export function getDecisionSourceDocumentLink(decisionId, signal) {
    return fetchSourceDocumentLink(`/cil/decisions/${decisionId}/document-url`, signal);
}
export async function listCilEventTypes(q, signal) {
    return apiFetch(`/cil/event-types${q ? `?q=${encodeURIComponent(q)}` : ""}`, { method: "GET", signal });
}
